import React from "react"
import { FormGroup, Input, Label } from "reactstrap"
import FilaCapacidad from "./FilaCapacidad"

// BDSJEA-353: ids de los elementos (labels y aria-describedby asociados).
export const ID_TITULO_LIMPIEZA = "titulo-limpieza-completa"
export const ID_CHECK_LIMPIEZA = "chk-limpiar-base-completa"
export const ID_ADVERTENCIA_LIMPIEZA = "advertencia-limpieza-completa"
export const ID_FRASE_LIMPIEZA = "txt-confirmacion-limpieza"

export const TEXTO_ADVERTENCIA_LIMPIEZA =
  "Se borrarán TODOS los productores, establecimientos, lotes, parcelas, proyecciones, evaluaciones, siembras y hacienda de este ambiente, incluidos los datos que no provienen de la migración (por ejemplo datos de prueba). Esta acción no se puede deshacer desde la aplicación. Se conservan: usuarios, configuración, dominios y auditoría. Durante la limpieza la aplicación puede quedar bloqueada unos 40 segundos."

// Comparación exacta (case-sensitive, sin recortar espacios). Sin frase pedida
// por el backend nunca coincide.
export const fraseCoincide = (frase, frasePedida) =>
  typeof frasePedida === "string" &&
  frasePedida.length > 0 &&
  frase === frasePedida

const BADGE_LIMPIEZA = { color: "danger", texto: "opt" }

const DESCRIPCION_LIMPIEZA = (
  <>
    <span className="d-block">
      Tildado: borra TODOS los productores, establecimientos y sus datos (lotes,
      parcelas, proyecciones, evaluaciones, siembras, hacienda), también los que
      no vienen de la importación. Conserva usuarios, configuración, dominios y
      auditoría.
    </span>
    <span className="d-block">
      Sin tildar: borra solo los productores y establecimientos de importaciones
      anteriores (y lo que depende de ellos) y los vuelve a importar.
    </span>
  </>
)

/**
 * Opción "Limpiar la base completa antes de importar" (fase IDLE), como tercera
 * fila de la lista de capacidades. Solo se muestra si el backend informa
 * `disponible === true`. Presentacional: el estado lo maneja quien lo usa.
 *
 * @param {object|null} disponibilidad { disponible, ambiente, frasePedida, motivo }
 */
export const FilaLimpiezaCompleta = ({
  disponibilidad,
  marcado,
  onMarcar,
  deshabilitada = false,
  nota = null,
}) => {
  if (disponibilidad?.disponible !== true) return null

  return (
    <FilaCapacidad
      badge={BADGE_LIMPIEZA}
      titulo="Limpiar la base completa antes de importar"
      descripcion={DESCRIPCION_LIMPIEZA}
      checkId={ID_CHECK_LIMPIEZA}
      checked={marcado}
      onChange={onMarcar}
      testId="limpieza-completa"
      deshabilitada={deshabilitada}
      nota={nota}
      tituloId={ID_TITULO_LIMPIEZA}
      inputProps={{
        "aria-labelledby": ID_TITULO_LIMPIEZA,
        "aria-describedby": marcado ? ID_ADVERTENCIA_LIMPIEZA : undefined,
      }}
    />
  )
}

/**
 * Detalle de la limpieza completa (va después de la lista): con
 * `disponible: false` y `motivo` solo se muestra el motivo, en un texto
 * discreto; con la opción marcada, la advertencia roja y el campo de frase.
 */
export const DetalleLimpiezaCompleta = ({
  disponibilidad,
  marcado,
  frase,
  onFrase,
}) => {
  if (!disponibilidad) return null

  if (disponibilidad.disponible !== true) {
    return disponibilidad.motivo ? (
      <small className="text-muted d-block mt-3" data-testid="limpieza-motivo">
        <i className="mdi mdi-information-outline me-1" aria-hidden="true" />
        {disponibilidad.motivo}
      </small>
    ) : null
  }

  if (!marcado) return null

  const { ambiente, frasePedida } = disponibilidad
  const coincide = fraseCoincide(frase, frasePedida)

  return (
    <div className="mt-3" data-testid="limpieza-completa-detalle">
      {/* No es un error: sin role="alert" (reservado a errores). */}
      <div
        id={ID_ADVERTENCIA_LIMPIEZA}
        className="alert alert-danger mb-3"
        data-testid="advertencia-limpieza"
      >
        <i className="mdi mdi-alert-octagon-outline me-2" aria-hidden="true" />
        <strong>
          Atención: se va a borrar toda la base
          {ambiente ? ` de ${ambiente}` : ""}.
        </strong>
        <p className="mb-0 mt-2">{TEXTO_ADVERTENCIA_LIMPIEZA}</p>
      </div>

      <FormGroup className="mb-0">
        <Label for={ID_FRASE_LIMPIEZA}>
          Para confirmar, escribí exactamente:{" "}
          <code data-testid="frase-pedida">{frasePedida}</code>
        </Label>
        <Input
          type="text"
          id={ID_FRASE_LIMPIEZA}
          value={frase}
          autoComplete="off"
          spellCheck={false}
          invalid={frase.length > 0 && !coincide}
          valid={coincide}
          aria-describedby={ID_ADVERTENCIA_LIMPIEZA}
          onChange={e => onFrase(e.target.value)}
        />
      </FormGroup>
    </div>
  )
}
